import { ReactNode } from "react";

import Tag from "@/components/common/Tag/Tag";
import { ClockWhiteIcon } from "@/components/icons/ClockWhiteIcon";
import { LockedBlackIcon } from "@/components/icons/LockedBlackIcon";
import { LockedWhiteIcon } from "@/components/icons/LockedWhiteIcon";
import { PlayBigIcon } from "@/components/icons/PlayBigIcon";
import { PlayWhiteIcon } from "@/components/icons/PlayWhiteIcon";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import type { NutrafolAcademyCourse } from "@/types/nutrafolAcademy";
import { cn } from "@/utils/general";

export interface CourseCardContentProps {
  course: NutrafolAcademyCourse;
  tagLabel?: string;
  actionLabel: string;
  locked?: boolean;
  /** Rendered below the action button */
  footer?: ReactNode;
}

const CourseCardContent = ({
  course,
  tagLabel,
  actionLabel,
  locked = false,
  footer,
}: CourseCardContentProps) => {
  const renderButton = (
    <>
      <ButtonNova
        label={actionLabel}
        width="100%"
        disabled={locked}
        icon="Left"
        customIcon={
          <span
            className={cn(
              "w-6 h-6 flex justify-center items-center rounded-sm bg-nova-transparency-black-20",
              !locked && "group-hover:bg-nova-brand-primary-80"
            )}
          >
            {locked ? <LockedWhiteIcon /> : <PlayWhiteIcon />}
          </span>
        }
        className={
          locked
            ? "!bg-nova-transparency-white-20 pointer-events-none text-nova-base-white"
            : "group hover:bg-nova-brand-primary-100 cursor-pointer"
        }
        copyClassName="!text-nova-base-white"
      />
      {footer}
    </>
  );

  return (
    <>
      <div className="flex gap-4 xl:gap-10">
        <div className="w-[85px] md:w-[112px] lg:w-[140px] rounded-sm overflow-hidden">
          <div className={cn("relative", locked && "pointer-events-none")}>
            <Picture
              src={course.image}
              alt={course.title}
              loading="lazy"
              className={cn("block w-full", locked && "blur-[2px]")}
            />
            <span className="absolute top-0 left-0 w-full h-full flex justify-center items-center">
              <span className="w-8 h-8 lg:w-12 lg:h-12 rounded-sm bg-nova-brand-accent-80 flex justify-center items-center">
                <span className="w-4 h-4 lg:w-6 lg:h-6 text-nova-base-black">
                  {locked ? (
                    <LockedBlackIcon width="100%" height="100%" />
                  ) : (
                    <PlayBigIcon width="100%" height="100%" />
                  )}
                </span>
              </span>
            </span>
          </div>
        </div>
        <div className="flex-1 flex flex-col my-auto md:my-0">
          <div className="flex mb-2 items-center gap-2">
            {tagLabel ? (
              <Tag
                data={{
                  line: tagLabel,
                  classes: "bg-nova-base-solid-40 !px-[6px] !py-1 h-5",
                }}
              />
            ) : null}
            <div className="flex">
              <div className="mr-[2px] text-nova-base-white">
                <ClockWhiteIcon />
              </div>
              <div>
                <TextElementNova
                  text={course.duration}
                  className="Caption-10-Dia-Normal text-nova-base-white !italic !leading-[17px]"
                />
              </div>
            </div>
          </div>
          <TextElementNova
            text={course.title}
            className="Title-10-Super-Normal text-nova-base-white mb-2"
          />
          <TextElementNova
            text={course.description}
            className="text-nova-base-white md:mb-2"
            classes={{
              default: "Body-10-Dia-Normal",
              1024: "Body-20-Dia-Normal",
              1500: "Body-30-Dia-Normal",
            }}
          />
          <div className="mt-auto hidden md:block">{renderButton}</div>
        </div>
      </div>
      <div className="md:hidden mt-4">{renderButton}</div>
    </>
  );
};

export default CourseCardContent;
